// LARGE COMPONENT
import { Button, Empty, message, Space, Typography } from 'antd';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import client from 'apolloClient';
import { DATE_FORMAT, TIME_FORMAT } from 'constants/dates';
import {
  APPOINTMENT_CREATOR,
  APPOINTMENT_STATUSES
} from 'constants/appointments';
import { PlusOutlined } from '@ant-design/icons';
import { useUIDispatchContext } from 'providers/UI';
import { useMutation, useQuery } from '@apollo/client';
import { APPROVE_DATE } from 'gql/appointments/mutation';
import { ApproveDate } from 'gql/appointments/__generated__/ApproveDate';
import { GET_APPOINTMENT_ITEM } from 'gql/appointments/queries';
import { AppointmentById } from 'gql/appointments/__generated__/AppointmentById';

import { UserItem } from 'components/shared';
import {
  StyledContainer,
  StyledDate,
  StyledDateContainer,
  StyledDatesContainer,
  StyledTime,
  UsersWrapper
} from './styled';
// import { money } from 'utils/price';
import { getFileUrl } from 'utils/file';
import { DateItemType, IDetailsProps } from './types';
import { showErrorMessage } from 'utils/showErrorMessage';

const { Title, Text } = Typography;

const AppointmentDetailsTab: FC<IDetailsProps> = ({ date, isVideoCall }) => {
  // translations
  const { t } = useTranslation();
  // navigation
  const params = useParams();
  // states
  const [selectedDate, setSelectedDate] = useState('');
  // context
  const { toggleModal } = useUIDispatchContext();
  // graphql
  const { data } = useQuery<AppointmentById>(GET_APPOINTMENT_ITEM, {
    skip: !params.id,
    variables: {
      id: params.id
    },
    fetchPolicy: 'cache-only'
  });

  const appointment = data?.appointment?.data;

  const [approveDate, { loading: isApproving }] = useMutation<ApproveDate>(
    APPROVE_DATE,
    {
      onCompleted() {
        message.success(t('appointments_details.successfully_approved'));

        client.refetchQueries({ include: [GET_APPOINTMENT_ITEM] });
      },
      onError: err => showErrorMessage(err)
    }
  );

  const myDates = useMemo(() => {
    if (!isVideoCall || !appointment?.dates || date) {
      return [];
    }

    return appointment.dates
      .filter(item => Number(item.sender) !== APPOINTMENT_CREATOR.patient)
      .reduce((acc: DateItemType[], val) => {
        const date = dayjs(val.date).format(DATE_FORMAT);
        const time = dayjs(val.date).format(TIME_FORMAT);

        const index = acc.findIndex(item => item.date === date);

        const timeItem = {
          id: val.id,
          label: time
        };

        if (index > -1) {
          acc[index].times.push(timeItem);

          return acc;
        }

        return [
          ...acc,
          {
            date,
            times: [timeItem]
          }
        ];
      }, []);
  }, [appointment?.dates, date, isVideoCall]);

  const patientDates = useMemo(() => {
    if (!isVideoCall || !appointment?.dates || date) {
      return [];
    }

    return appointment.dates
      .filter(item => Number(item.sender) !== APPOINTMENT_CREATOR.doctor)
      .reduce((acc: DateItemType[], val) => {
        const date = dayjs(val.date).format(DATE_FORMAT);
        const time = dayjs(val.date).format(TIME_FORMAT);

        const index = acc.findIndex(item => item.date === date);

        const timeItem = {
          id: val.id,
          label: time
        };

        if (index > -1) {
          acc[index].times.push(timeItem);

          return acc;
        }

        return [
          ...acc,
          {
            date,
            times: [timeItem]
          }
        ];
      }, []);
  }, [appointment?.dates, date, isVideoCall]);

  const onClickSave = useCallback(() => {
    approveDate({
      variables: {
        appointmentId: params.id,
        dateId: selectedDate
      }
    });
  }, [approveDate, params.id, selectedDate]);

  if (!appointment) return null;

  const appointmentTranslatorInfo = appointment.appointment_translators?.[0];

  return (
    <Space direction="vertical" size={32} style={{ width: '100%' }}>
      {appointmentTranslatorInfo?.translator?.first_name &&
        appointmentTranslatorInfo.translator.last_name && (
          <UsersWrapper>
            <UserItem
              url={
                getFileUrl(
                  appointmentTranslatorInfo.translator.avatar
                ) as string
              }
              name={`${appointmentTranslatorInfo.translator.first_name} ${appointmentTranslatorInfo.translator.last_name}`}
              info={[
                `${t('appointments_details.translator_id')} ${
                  appointmentTranslatorInfo.translator.id
                }`,
                `${appointmentTranslatorInfo.translator_plan?.lang_1} / ${appointmentTranslatorInfo.translator_plan?.lang_2}`
              ]}
              circle
            />
          </UsersWrapper>
        )}
      <>
        {!date &&
          APPOINTMENT_STATUSES.rejected !== appointment.status &&
          isVideoCall && (
            <StyledDatesContainer>
              <Space direction="vertical" size={42} style={{ width: '100%' }}>
                <div>
                  <Space
                    size={24}
                    direction="vertical"
                    style={{ width: '100%', paddingBottom: 30 }}
                  >
                    <div>
                      <Title level={3} style={{ marginBottom: 12 }}>
                        {t('appointments_details.my_dates')}
                      </Title>
                      <Text style={{ marginBottom: 24 }}>
                        {t('appointments_details.select_dates')}
                      </Text>
                    </div>
                    {selectedDate ? (
                      <Button
                        type="primary"
                        onClick={onClickSave}
                        loading={isApproving}
                      >
                        {t('common.save')}
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        onClick={() =>
                          toggleModal('appointmentOption', true, {
                            id: params.id as string
                          })
                        }
                      >
                        <PlusOutlined />{' '}
                        {t('appointments_details.add_your_option')}
                      </Button>
                    )}
                  </Space>
                  <Space
                    size={24}
                    wrap
                    direction="vertical"
                    style={{ width: '100%' }}
                  >
                    {myDates.length
                      ? myDates.map(item => {
                          return (
                            <StyledContainer key={`date-item-${item.date}`}>
                              <StyledDateContainer>
                                <StyledDate>{item.date}</StyledDate>
                              </StyledDateContainer>
                              <Space size={8} wrap style={{ paddingLeft: 20 }}>
                                {item.times.map(time => {
                                  return (
                                    <StyledTime
                                      key={`time-item-${item.date}-${time.id}`}
                                      active={selectedDate === time.id}
                                      disabled
                                    >
                                      {time.label}
                                    </StyledTime>
                                  );
                                })}
                              </Space>
                            </StyledContainer>
                          );
                        })
                      : null}
                  </Space>
                </div>
                <div>
                  <Title level={3} style={{ marginBottom: 24 }}>
                    {t('appointments_details.patient_dates')}
                  </Title>
                  <Space
                    size={24}
                    wrap
                    direction="vertical"
                    style={{ width: '100%' }}
                  >
                    {patientDates.length ? (
                      patientDates.map(item => {
                        return (
                          <StyledContainer key={`date-item-${item.date}`}>
                            <StyledDateContainer>
                              <StyledDate>{item.date}</StyledDate>
                            </StyledDateContainer>
                            <Space size={8} wrap style={{ paddingLeft: 20 }}>
                              {item.times.map(time => {
                                return (
                                  <StyledTime
                                    key={`time-item-${item.date}-${time.id}`}
                                    active={selectedDate === time.id}
                                    onClick={() =>
                                      setSelectedDate(prev => {
                                        if (prev === time.id) return '';

                                        return time.id;
                                      })
                                    }
                                  >
                                    {time.label}
                                  </StyledTime>
                                );
                              })}
                            </Space>
                          </StyledContainer>
                        );
                      })
                    ) : (
                      <Empty />
                    )}
                  </Space>
                </div>
              </Space>
            </StyledDatesContainer>
          )}
      </>
    </Space>
  );
};

export default memo(AppointmentDetailsTab);
